/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import * as _ from 'lodash';

import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {AdnApiClientService} from '../../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../../common/controller/signal-admin-resource-api';

export interface UserModel extends SignalAdminResourceModel {
  username?: string;
  password?: string;
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class UserApiService extends SignalAdminResourceApiBase<UserModel> {
  constructor(
    client: AdnApiClientService,
    private readonly localNetworkProfile: LocalNetworkProfile,
    private readonly localUserPermissions: LocalUserPermissions,
    private readonly localUserProfile: LocalUserProfile
  ) {
    super(client, 'users', {}, () => this.localUserProfile.isReadOnly());
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<UserModel>> {
    const networkId = this.localNetworkProfile.getNetworkId();
    return super.list(params).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map((item) => {
        const roles = _.find(_.get(item, ['userRoles', 'rolesByNetwork']), (rolesByNetwork: any) => {
          return _.get(rolesByNetwork, ['network', 'id']) === this.localNetworkProfile.get('networkId');
        });
        const roleEntries = _.get(roles, 'roles');
        return {
          ...item,
          inNetwork: this.localUserPermissions.userPartOf(item, networkId),
          obfuscated: item.username.indexOf('@') < 0 && item.username.indexOf('*') > 0,
          networkRole: _.filter(roleEntries, (role: any) => !role.team),
          teamRoles: _.filter(roleEntries, (role: any) => role.team),
          teamGroupRoles: _.filter(roleEntries, (role: any) => role.teamGroup)
        };
      })
    })));
  }

  override save(model: UserModel): Observable<UserModel> {
    const submitted = _.trim(model.password).length === 0 ? _.omit(model, 'password') as UserModel : model;
    return super.save(submitted);
  }

  activateAppTwoFactorAuth(userId: string, twoFactorAuthCode: string, emailTwoFactorAuthCode: string): Observable<UserModel> {
    return this.client.request<UserModel>({
      method: 'POST',
      url: this.resourceUri(['users', userId]),
      params: {concise: true, twoFactorAuthCode, emailTwoFactorAuthCode},
      body: {id: userId, twoFactorAuthType: 'APP', login2faRequired: true}
    });
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<UserModel>> {
    return this.client.request<SignalAdminResourcePage<UserModel>>({
      method: 'GET',
      url: this.resourceUri('users'),
      params
    });
  }

  getLoginStats(userId?: string): Observable<any[]> {
    const uriParts = ['loginstats'];
    if (userId) {
      uriParts.push(userId);
    }
    return this.client.request<any[]>({
      method: 'GET',
      url: this.resourceUri(uriParts),
      params: {format: 'json'}
    });
  }
}
